import { vercel } from '@t3-oss/env-core/presets-zod';
import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

export const env = createEnv({
  extends: [vercel()],
  server: {
    // Creator activation target organization (Clerk)
    CREATOR_ORG_ID: z.string().optional(),
    CREATOR_ORG_ROLE: z.string().optional(),
    // Welcome template project id (used to seed new-user project content)
    WELCOME_TEMPLATE_PROJECT_ID: z.string().uuid().optional(),
    // Clerk Webhooks
    CLERK_WEBHOOK_SECRET: z.string().min(1),
    // Hookdeck Webhook
    HOOKDECK_WEBHOOK_SECRET: z.string().optional(),
    // optional debug removed from usage; keep minimal

    // Billing config (plan slugs -> periodic credits map) and UI labels
    // 欢迎积分：符合资格用户额度（字符串数字，可为空，默认 200）
    WELCOME_CREDITS: z.string().optional(),
    // 欢迎积分：不符合资格用户额度（字符串数字，可为空，默认 10）
    WELCOME_INELIGIBLE_CREDITS: z.string().optional(),
    // 欢迎积分：需要检查前缀的邮箱域名白名单（逗号分隔，如 "gmail.com,outlook.com"）
    // 不在白名单内的域名直接视为不合资格；为空则对所有域名检查前缀
    WELCOME_EMAIL_DOMAIN_ALLOWLIST: z.string().optional(),
    APP_BASE_URL: z.string().url().min(1),
    UPSTASH_REDIS_REST_URL: z.string().url().min(1),
    UPSTASH_REDIS_REST_TOKEN: z.string().min(1),

    STRIPE_SECRET_KEY: z.string().min(1).startsWith('sk_'),
    // Stripe subscription products removed; keep only one-off credits
    STRIPE_WEBHOOK_SECRET: z.string().min(1).startsWith('whsec_'),
    // Stripe subscription (Checkout) default price id (optional, for test UI)
    STRIPE_SUBSCRIPTION_PRICE_ID: z.string().min(1).startsWith('price_').optional(),
    // Per-plan subscription price IDs (resolved at runtime via subscription_plans table)
    STRIPE_SUBSCRIPTION_PRICE_ID_STARTER: z.string().min(1).startsWith('price_').optional(),
    STRIPE_SUBSCRIPTION_PRICE_ID_PRO: z.string().min(1).startsWith('price_').optional(),
    STRIPE_SUBSCRIPTION_PRICE_ID_MAX: z.string().min(1).startsWith('price_').optional(),
    STRIPE_SUBSCRIPTION_PRICE_ID_ULTRA: z.string().min(1).startsWith('price_').optional(),
    // Yearly subscription price IDs
    STRIPE_SUBSCRIPTION_PRICE_ID_STARTER_YEARLY: z.string().min(1).startsWith('price_').optional(),
    STRIPE_SUBSCRIPTION_PRICE_ID_PRO_YEARLY: z.string().min(1).startsWith('price_').optional(),
    STRIPE_SUBSCRIPTION_PRICE_ID_MAX_YEARLY: z.string().min(1).startsWith('price_').optional(),
    STRIPE_SUBSCRIPTION_PRICE_ID_ULTRA_YEARLY: z.string().min(1).startsWith('price_').optional(),
    // Stripe subscription plans UI config (JSON string). Drives /api/billing/plans response + plan cards UI.
    STRIPE_SUBSCRIPTION_PLANS_JSON: z.string().optional(),
    // Credits one-off purchase
    STRIPE_CREDITS_PRODUCT_ID: z.string().min(1).startsWith('prod_').optional(),
    STRIPE_CREDITS_PRICE_ID: z.string().min(1).startsWith('price_').optional(),
    // Tiered credits prices (optional): used by /api/credits/purchase?amount=...
    STRIPE_CREDITS_PRICE_ID_TIER_1: z.string().min(1).startsWith('price_').optional(),
    STRIPE_CREDITS_PRICE_ID_TIER_2: z.string().min(1).startsWith('price_').optional(),
    STRIPE_CREDITS_PRICE_ID_TIER_3: z.string().min(1).startsWith('price_').optional(),
    STRIPE_CREDITS_PRICE_ID_TIER_4: z.string().min(1).startsWith('price_').optional(),
    STRIPE_CREDITS_PRICE_ID_TIER_5: z.string().min(1).startsWith('price_').optional(),
    STRIPE_CREDITS_PRICE_ID_TIER_6: z.string().min(1).startsWith('price_').optional(),

    // Unified Stripe Product ID for model pass purchases (used with price_data)
    STRIPE_MODEL_PASS_PRODUCT_ID: z.string().min(1).startsWith('prod_').optional(),

    // Credits bundles config override (JSON string). If set, overrides file config
    CREDITS_BUNDLES_JSON: z.string().optional(),

    // Supabase Integration
    POSTGRES_URL: z.string().url().min(1),
    SUPABASE_SERVICE_ROLE_KEY: z.string().min(1),

    // AI SDK
    OPENAI_API_KEY: z.string().min(1).startsWith('sk-'),
    GOOGLE_GENERATIVE_AI_API_KEY: z.string().min(1),
    GROQ_API_KEY: z.string().min(1).startsWith('gsk_'),
    DEEPSEEK_API_KEY: z.string().min(1).startsWith('sk-'),
    ANTHROPIC_API_KEY: z.string().min(1).startsWith('sk-'),
    XAI_API_KEY: z.string().min(1).startsWith('xai-'),
    AWS_ACCESS_KEY_ID: z.string().min(1),
    AWS_SECRET_ACCESS_KEY: z.string().min(1),
    AWS_REGION: z.string().min(1),
    FAL_API_KEY: z.string().min(1),
    TOGETHER_AI_API_KEY: z.string().min(1).startsWith('tgp_v1_'),
    COHERE_API_KEY: z.string().min(1),
    VERCEL_API_KEY: z.string().min(1).startsWith('v1:'),
    MISTRAL_API_KEY: z.string().min(1),
    HUME_API_KEY: z.string().min(1),
    LMNT_API_KEY: z.string().min(1),
    PERPLEXITY_API_KEY: z.string().min(1).startsWith('pplx-'),
    REPLICATE_API_TOKEN: z.string().min(1).startsWith('r8_'),

    // Other Models
    MINIMAX_GROUP_ID: z.string().min(1),
    MINIMAX_API_KEY: z.string().min(1),
    RUNWAYML_API_SECRET: z.string().min(1).startsWith('key_'),
    LUMA_API_KEY: z.string().min(1).startsWith('luma-'),
    // Credit conversion: USD per credit (e.g., 0.005 USD => 1 credit costs $0.005)
    CREDIT_VALUE_USD: z.string().min(1),
    // 固定奖励（仅角色专用）
    INVITER_FIXED_REWARD: z.string().optional(),
    INVITEE_FIXED_REWARD: z.string().optional(),
    // 邀请奖励上限人次（仅限制对邀请人的奖励次数；缺省为不限）
    INVITER_MAX_INVITES: z.string().optional(),
    // 邀请人充值返现比例（小数，如 0.1 表示 10%）
    INVITER_CASHBACK_PERCENT: z.string().optional(),
    // 个人并发任务默认上限（未设 metadata.maxActiveTasks 时的回退值）
    DEFAULT_MAX_ACTIVE_TASKS: z.string().optional(),
    // 团队并发任务默认上限（未设 org_profile.metadata.maxActiveTasks 时的回退值）
    DEFAULT_ORG_MAX_ACTIVE_TASKS: z.string().optional(),
    // Google VEO
    GOOGLE_VEO_API_KEY: z.string().min(1).optional(),
    GOOGLE_VEO_BASE_URL: z.string().url().min(1).optional(),
    BF_API_KEY: z.string().min(1),
    MIDJOURNEY_API_KEY: z.string().min(1),
    // Liblib API
    LIBLIB_ACCESS_KEY: z.string().min(1).optional(),
    LIBLIB_SECRET_KEY: z.string().min(1).optional(),
    LIBLIB_BASE_URL: z.string().url().min(1).optional(),
    // Internal header key to restrict access to server/backends
    LIBLIB_INTERNAL_KEY: z.string().min(1).optional(),
    // Feature flags (server-only)
    // When set to 'true' or 'false', controls auto description after text-to-image generation
    IMAGE_TEXT_TO_IMAGE_AUTO_DESCRIBE_ENABLED: z.string().optional(),

    // PromptLayer (optional)
    PROMPTLAYER_API_KEY: z.string().optional(),
    PROMPTLAYER_TEMPLATE_NAME: z.string().optional(),
    PROMPTLAYER_TEMPLATE_VERSION: z.string().optional(),
    PROMPTLAYER_TEMPLATE_LABEL: z.string().optional(),
    PROMPTLAYER_PROVIDER: z.string().optional(),
    PROMPTLAYER_WORKSPACE_ID: z.string().optional(),
    // Share snapshot fallback: when 'true', allow cloning/display to fall back to latest content if snapshot is missing
    SHARE_SNAPSHOT_FALLBACK_ALLOWED: z.string().optional(),
    // Upload storage provider switch (fast migration): 'supabase' | 'oss'
    UPLOAD_STORAGE_PROVIDER: z.enum(['supabase', 'oss']).optional(),

    // LLM 网关(new-api):Stripe llm_wallet 充值 webhook 依赖 admin 凭证
    // optional:未配置时充值链路报错(流水标 failed),不影响其他 webhook 分支
    NEWAPI_BASE_URL: z.string().url().min(1).optional(),
    NEWAPI_ADMIN_TOKEN: z.string().min(1).optional(),
    NEWAPI_ADMIN_USER_ID: z.string().min(1).optional(),

    // Azure Video Indexer (server-only)
    AZURE_VIDEO_INDEXER_ACCOUNT_ID: z.string().min(1),
    AZURE_VIDEO_INDEXER_SUBSCRIPTION_KEY: z.string().min(1),
    AZURE_VIDEO_INDEXER_LOCATION: z.string().min(1),
    // Optional: if provided, skips automatic token retrieval
    AZURE_VIDEO_INDEXER_ACCESS_TOKEN: z.string().optional(),
  },
  client: {
    NEXT_PUBLIC_TURNSTILE_SITE_KEY: z.string().min(1),
    // Meta (Facebook) Pixel ID（购买成功页懒加载上报 Purchase；缺省时不注入不上报；与 Studio 配同一值）
    NEXT_PUBLIC_META_PIXEL_ID: z.string().optional(),
    // PostHog removed
    // Optional: special credits bundle (Product ID) exposed to client
    NEXT_PUBLIC_CREDITS_BUNDLE_PRODUCT_ID: z.string().optional(),
    // Highest plan slug: when user is on this plan, hide Upgrade and only show Buy Credits (optional)
    NEXT_PUBLIC_HIGHEST_PLAN_SLUG: z.string().optional(),
    // Advanced image options required plan slug (optional)
    NEXT_PUBLIC_ADVANCED_IMAGE_PLAN_SLUG: z.string().optional(),
    // Advanced video options required plan slug (optional)
    NEXT_PUBLIC_ADVANCED_VIDEO_PLAN_SLUG: z.string().optional(),
    // Toggle Markdown features in text editor ("true" to enable)
    NEXT_PUBLIC_ENABLE_MARKDOWN: z.string().optional(),
    // Comma-separated node types to show in toolbar (e.g. "text,image,video,audio,file,code,tweet")
    NEXT_PUBLIC_ENABLED_NODE_TYPES: z.string().optional(),
    // Feature flags (client-exposed)
    // When set to 'true' or 'false', controls auto description on image primitive upload/import
    NEXT_PUBLIC_IMAGE_PRIMITIVE_AUTO_DESCRIBE_ENABLED: z.string().optional(),
    // Hide Tools button/panel when set to 'true'
    NEXT_PUBLIC_HIDE_TOOLS: z.string().optional(),
    // Hide bottom canvas prompt bar when set to 'true'
    NEXT_PUBLIC_HIDE_CANVAS_PROMPT_BAR: z.string().optional(),
    // Default node style for text/image/video: 'custom' | 'none'
    NEXT_PUBLIC_DEFAULT_NODE_STYLE: z.string().optional(),
    // Disable global drag-and-drop to create nodes overlay when set to 'true'
    NEXT_PUBLIC_DISABLE_NODE_DROP: z.string().optional(),
    // Skip concurrency check when set to 'true'
    NEXT_PUBLIC_SKIP_CONCURRENCY_CHECK: z.string().optional(),
    // Enable realtime generation updates when 'true' (default true if unset)
    NEXT_PUBLIC_ENABLE_REALTIME: z.string().optional(),
    // Enable polling fallbacks when 'true' (default true if unset)
    NEXT_PUBLIC_ENABLE_POLLING: z.string().optional(),
    // Enable project-level realtime updates when 'true' (default true if unset)
    NEXT_PUBLIC_ENABLE_PROJECT_REALTIME: z.string().optional(),

    // Supabase Integration
    NEXT_PUBLIC_SUPABASE_URL: z.string().url().min(1),
    NEXT_PUBLIC_SUPABASE_ANON_KEY: z.string().min(1),

    // Clerk redirects (optional, use per Clerk docs)
    NEXT_PUBLIC_CLERK_SIGN_IN_URL: z.string().optional(),
    NEXT_PUBLIC_CLERK_SIGN_UP_URL: z.string().optional(),
    NEXT_PUBLIC_CLERK_SIGN_IN_FORCE_REDIRECT_URL: z.string().optional(),
    NEXT_PUBLIC_CLERK_SIGN_UP_FORCE_REDIRECT_URL: z.string().optional(),
    NEXT_PUBLIC_CLERK_SIGN_IN_FALLBACK_REDIRECT_URL: z.string().optional(),
    NEXT_PUBLIC_CLERK_SIGN_UP_FALLBACK_REDIRECT_URL: z.string().optional(),
    // External editor base URL (required)
    NEXT_PUBLIC_EDITOR_URL: z.string().url().min(1),
    // Switch video generation mode: 'api' | 'action' (default 'api')
    NEXT_PUBLIC_VIDEO_GENERATE_MODE: z.enum(['api', 'action']).optional(),
    // Portal URL for external docs/archives
    NEXT_PUBLIC_PORTAL_URL: z.string().url().min(1).optional(),
    // WeChat group QR code image URL
    NEXT_PUBLIC_WECHAT_QR_URL: z.string().url().min(1).optional(),
    // Discord invite link
    NEXT_PUBLIC_DISCORD_URL: z.string().url().min(1).optional(),
    // Discord QR code image URL
    NEXT_PUBLIC_DISCORD_QR_URL: z.string().url().min(1).optional(),
    // Business WeChat QR code image URL (for procurement/bulk purchase)
    NEXT_PUBLIC_BUSINESS_WECHAT_QR_URL: z.string().url().min(1).optional(),
    // Hide Flowise bubble button when set to 'true'
    NEXT_PUBLIC_FLOWISE_HIDE_BUTTON: z.string().optional(),
    // Target organization to activate after creator verification
    NEXT_PUBLIC_CREATOR_ORG_ID: z.string().optional(),
    // Shared secret for lightweight internal API auth (browser → API routes)
    NEXT_PUBLIC_INTERNAL_API_SECRET: z.string().min(1),
    // Auto backup: how many successful saves before creating an auto backup (default 10)
    NEXT_PUBLIC_AUTO_BACKUP_SAVE_COUNT: z.string().optional(),
    // Cover snapshot: minimum interval between cover screenshot attempts (ms). Default handled in code.
    NEXT_PUBLIC_COVER_SNAPSHOT_THROTTLE_MS: z.string().optional(),
    // When unset / not 'false', off-screen video/image src is set to undefined to free memory.
    // Set to 'false' to keep all media loaded regardless of viewport.
    NEXT_PUBLIC_UNLOAD_OFFSCREEN_MEDIA: z.string().optional(),
  },
  runtimeEnv: {
    CREATOR_ORG_ID: process.env.CREATOR_ORG_ID,
    CREATOR_ORG_ROLE: process.env.CREATOR_ORG_ROLE,
    CLERK_WEBHOOK_SECRET: process.env.CLERK_WEBHOOK_SECRET,
    HOOKDECK_WEBHOOK_SECRET: process.env.HOOKDECK_WEBHOOK_SECRET,
    // no debug var consumed
    WELCOME_CREDITS: process.env.WELCOME_CREDITS,
    WELCOME_INELIGIBLE_CREDITS: process.env.WELCOME_INELIGIBLE_CREDITS,
    WELCOME_EMAIL_DOMAIN_ALLOWLIST: process.env.WELCOME_EMAIL_DOMAIN_ALLOWLIST,
    APP_BASE_URL: process.env.APP_BASE_URL,
    OPENAI_API_KEY: process.env.OPENAI_API_KEY,
    GOOGLE_GENERATIVE_AI_API_KEY: process.env.GOOGLE_GENERATIVE_AI_API_KEY,
    GROQ_API_KEY: process.env.GROQ_API_KEY,
    DEEPSEEK_API_KEY: process.env.DEEPSEEK_API_KEY,
    ANTHROPIC_API_KEY: process.env.ANTHROPIC_API_KEY,
    XAI_API_KEY: process.env.XAI_API_KEY,
    AWS_ACCESS_KEY_ID: process.env.AWS_ACCESS_KEY_ID,
    AWS_SECRET_ACCESS_KEY: process.env.AWS_SECRET_ACCESS_KEY,
    AWS_REGION: process.env.AWS_REGION,
    FAL_API_KEY: process.env.FAL_API_KEY,
    TOGETHER_AI_API_KEY: process.env.TOGETHER_AI_API_KEY,
    COHERE_API_KEY: process.env.COHERE_API_KEY,
    MISTRAL_API_KEY: process.env.MISTRAL_API_KEY,
    POSTGRES_URL: process.env.POSTGRES_URL,
    NEXT_PUBLIC_SUPABASE_URL: process.env.NEXT_PUBLIC_SUPABASE_URL,
    NEXT_PUBLIC_SUPABASE_ANON_KEY: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY,
    NEXT_PUBLIC_TURNSTILE_SITE_KEY: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY,
    NEXT_PUBLIC_META_PIXEL_ID: process.env.NEXT_PUBLIC_META_PIXEL_ID,
    NEXT_PUBLIC_CLERK_SIGN_IN_URL: process.env.NEXT_PUBLIC_CLERK_SIGN_IN_URL,
    NEXT_PUBLIC_CLERK_SIGN_UP_URL: process.env.NEXT_PUBLIC_CLERK_SIGN_UP_URL,
    NEXT_PUBLIC_CLERK_SIGN_IN_FORCE_REDIRECT_URL: process.env.NEXT_PUBLIC_CLERK_SIGN_IN_FORCE_REDIRECT_URL,
    NEXT_PUBLIC_CLERK_SIGN_UP_FORCE_REDIRECT_URL: process.env.NEXT_PUBLIC_CLERK_SIGN_UP_FORCE_REDIRECT_URL,
    NEXT_PUBLIC_CLERK_SIGN_IN_FALLBACK_REDIRECT_URL: process.env.NEXT_PUBLIC_CLERK_SIGN_IN_FALLBACK_REDIRECT_URL,
    NEXT_PUBLIC_CLERK_SIGN_UP_FALLBACK_REDIRECT_URL: process.env.NEXT_PUBLIC_CLERK_SIGN_UP_FALLBACK_REDIRECT_URL,
    NEXT_PUBLIC_EDITOR_URL: process.env.NEXT_PUBLIC_EDITOR_URL,
    NEXT_PUBLIC_VIDEO_GENERATE_MODE: process.env.NEXT_PUBLIC_VIDEO_GENERATE_MODE,
    NEXT_PUBLIC_PORTAL_URL: process.env.NEXT_PUBLIC_PORTAL_URL,
    NEXT_PUBLIC_WECHAT_QR_URL: process.env.NEXT_PUBLIC_WECHAT_QR_URL,
    NEXT_PUBLIC_DISCORD_URL: process.env.NEXT_PUBLIC_DISCORD_URL,
    NEXT_PUBLIC_DISCORD_QR_URL: process.env.NEXT_PUBLIC_DISCORD_QR_URL,
    NEXT_PUBLIC_BUSINESS_WECHAT_QR_URL: process.env.NEXT_PUBLIC_BUSINESS_WECHAT_QR_URL,
    NEXT_PUBLIC_FLOWISE_HIDE_BUTTON: process.env.NEXT_PUBLIC_FLOWISE_HIDE_BUTTON,
    NEXT_PUBLIC_CREATOR_ORG_ID: process.env.NEXT_PUBLIC_CREATOR_ORG_ID,
    NEXT_PUBLIC_AUTO_BACKUP_SAVE_COUNT: process.env.NEXT_PUBLIC_AUTO_BACKUP_SAVE_COUNT,
    NEXT_PUBLIC_COVER_SNAPSHOT_THROTTLE_MS: process.env.NEXT_PUBLIC_COVER_SNAPSHOT_THROTTLE_MS,
    NEXT_PUBLIC_UNLOAD_OFFSCREEN_MEDIA: process.env.NEXT_PUBLIC_UNLOAD_OFFSCREEN_MEDIA,
    NEXT_PUBLIC_INTERNAL_API_SECRET: process.env.NEXT_PUBLIC_INTERNAL_API_SECRET,
    NEXT_PUBLIC_HIDE_TOOLS: process.env.NEXT_PUBLIC_HIDE_TOOLS,
    NEXT_PUBLIC_HIDE_CANVAS_PROMPT_BAR: process.env.NEXT_PUBLIC_HIDE_CANVAS_PROMPT_BAR,
    NEXT_PUBLIC_DEFAULT_NODE_STYLE: process.env.NEXT_PUBLIC_DEFAULT_NODE_STYLE,
    NEXT_PUBLIC_DISABLE_NODE_DROP: process.env.NEXT_PUBLIC_DISABLE_NODE_DROP,
    NEXT_PUBLIC_SKIP_CONCURRENCY_CHECK: process.env.NEXT_PUBLIC_SKIP_CONCURRENCY_CHECK,
    NEXT_PUBLIC_ENABLE_REALTIME: process.env.NEXT_PUBLIC_ENABLE_REALTIME,
    NEXT_PUBLIC_ENABLE_POLLING: process.env.NEXT_PUBLIC_ENABLE_POLLING,
    NEXT_PUBLIC_ENABLE_PROJECT_REALTIME: process.env.NEXT_PUBLIC_ENABLE_PROJECT_REALTIME,
    MINIMAX_GROUP_ID: process.env.MINIMAX_GROUP_ID,
    MINIMAX_API_KEY: process.env.MINIMAX_API_KEY,
    UPSTASH_REDIS_REST_URL: process.env.UPSTASH_REDIS_REST_URL,
    UPSTASH_REDIS_REST_TOKEN: process.env.UPSTASH_REDIS_REST_TOKEN,
    STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY,
    // removed subscription product IDs
    STRIPE_WEBHOOK_SECRET: process.env.STRIPE_WEBHOOK_SECRET,
    STRIPE_SUBSCRIPTION_PRICE_ID: process.env.STRIPE_SUBSCRIPTION_PRICE_ID,
    STRIPE_SUBSCRIPTION_PRICE_ID_STARTER: process.env.STRIPE_SUBSCRIPTION_PRICE_ID_STARTER,
    STRIPE_SUBSCRIPTION_PRICE_ID_PRO: process.env.STRIPE_SUBSCRIPTION_PRICE_ID_PRO,
    STRIPE_SUBSCRIPTION_PRICE_ID_MAX: process.env.STRIPE_SUBSCRIPTION_PRICE_ID_MAX,
    STRIPE_SUBSCRIPTION_PRICE_ID_ULTRA: process.env.STRIPE_SUBSCRIPTION_PRICE_ID_ULTRA,
    STRIPE_SUBSCRIPTION_PRICE_ID_STARTER_YEARLY: process.env.STRIPE_SUBSCRIPTION_PRICE_ID_STARTER_YEARLY,
    STRIPE_SUBSCRIPTION_PRICE_ID_PRO_YEARLY: process.env.STRIPE_SUBSCRIPTION_PRICE_ID_PRO_YEARLY,
    STRIPE_SUBSCRIPTION_PRICE_ID_MAX_YEARLY: process.env.STRIPE_SUBSCRIPTION_PRICE_ID_MAX_YEARLY,
    STRIPE_SUBSCRIPTION_PRICE_ID_ULTRA_YEARLY: process.env.STRIPE_SUBSCRIPTION_PRICE_ID_ULTRA_YEARLY,
    STRIPE_SUBSCRIPTION_PLANS_JSON: process.env.STRIPE_SUBSCRIPTION_PLANS_JSON,
    STRIPE_CREDITS_PRODUCT_ID: process.env.STRIPE_CREDITS_PRODUCT_ID,
    STRIPE_CREDITS_PRICE_ID: process.env.STRIPE_CREDITS_PRICE_ID,
    STRIPE_CREDITS_PRICE_ID_TIER_1: process.env.STRIPE_CREDITS_PRICE_ID_TIER_1,
    STRIPE_CREDITS_PRICE_ID_TIER_2: process.env.STRIPE_CREDITS_PRICE_ID_TIER_2,
    STRIPE_CREDITS_PRICE_ID_TIER_3: process.env.STRIPE_CREDITS_PRICE_ID_TIER_3,
    STRIPE_CREDITS_PRICE_ID_TIER_4: process.env.STRIPE_CREDITS_PRICE_ID_TIER_4,
    STRIPE_CREDITS_PRICE_ID_TIER_5: process.env.STRIPE_CREDITS_PRICE_ID_TIER_5,
    STRIPE_CREDITS_PRICE_ID_TIER_6: process.env.STRIPE_CREDITS_PRICE_ID_TIER_6,
    STRIPE_MODEL_PASS_PRODUCT_ID: process.env.STRIPE_MODEL_PASS_PRODUCT_ID,
    CREDITS_BUNDLES_JSON: process.env.CREDITS_BUNDLES_JSON,
    // removed: STRIPE_CREDITS_PER_UNIT (now rely on Stripe Price metadata only)
    SUPABASE_SERVICE_ROLE_KEY: process.env.SUPABASE_SERVICE_ROLE_KEY,
    RUNWAYML_API_SECRET: process.env.RUNWAYML_API_SECRET,
    LUMA_API_KEY: process.env.LUMA_API_KEY,
    GOOGLE_VEO_API_KEY: process.env.GOOGLE_VEO_API_KEY,
    GOOGLE_VEO_BASE_URL: process.env.GOOGLE_VEO_BASE_URL,
    NEXT_PUBLIC_CREDITS_BUNDLE_PRODUCT_ID: process.env.NEXT_PUBLIC_CREDITS_BUNDLE_PRODUCT_ID,
    NEXT_PUBLIC_HIGHEST_PLAN_SLUG: process.env.NEXT_PUBLIC_HIGHEST_PLAN_SLUG,
    NEXT_PUBLIC_ADVANCED_IMAGE_PLAN_SLUG: process.env.NEXT_PUBLIC_ADVANCED_IMAGE_PLAN_SLUG,
    NEXT_PUBLIC_ADVANCED_VIDEO_PLAN_SLUG: process.env.NEXT_PUBLIC_ADVANCED_VIDEO_PLAN_SLUG,
    NEXT_PUBLIC_ENABLE_MARKDOWN: process.env.NEXT_PUBLIC_ENABLE_MARKDOWN,
    NEXT_PUBLIC_ENABLED_NODE_TYPES: process.env.NEXT_PUBLIC_ENABLED_NODE_TYPES,
    VERCEL_API_KEY: process.env.VERCEL_API_KEY,
    HUME_API_KEY: process.env.HUME_API_KEY,
    LMNT_API_KEY: process.env.LMNT_API_KEY,
    BF_API_KEY: process.env.BF_API_KEY,
    PERPLEXITY_API_KEY: process.env.PERPLEXITY_API_KEY,
    REPLICATE_API_TOKEN: process.env.REPLICATE_API_TOKEN,
    MIDJOURNEY_API_KEY: process.env.MIDJOURNEY_API_KEY,
    LIBLIB_ACCESS_KEY: process.env.LIBLIB_ACCESS_KEY,
    LIBLIB_SECRET_KEY: process.env.LIBLIB_SECRET_KEY,
    LIBLIB_BASE_URL: process.env.LIBLIB_BASE_URL,
    LIBLIB_INTERNAL_KEY: process.env.LIBLIB_INTERNAL_KEY,
    IMAGE_TEXT_TO_IMAGE_AUTO_DESCRIBE_ENABLED: process.env.IMAGE_TEXT_TO_IMAGE_AUTO_DESCRIBE_ENABLED,
    NEXT_PUBLIC_IMAGE_PRIMITIVE_AUTO_DESCRIBE_ENABLED: process.env.NEXT_PUBLIC_IMAGE_PRIMITIVE_AUTO_DESCRIBE_ENABLED,
    CREDIT_VALUE_USD: process.env.CREDIT_VALUE_USD,
    INVITER_FIXED_REWARD: process.env.INVITER_FIXED_REWARD,
    INVITEE_FIXED_REWARD: process.env.INVITEE_FIXED_REWARD,
    INVITER_MAX_INVITES: process.env.INVITER_MAX_INVITES,
    INVITER_CASHBACK_PERCENT: process.env.INVITER_CASHBACK_PERCENT,
    DEFAULT_MAX_ACTIVE_TASKS: process.env.DEFAULT_MAX_ACTIVE_TASKS,
    DEFAULT_ORG_MAX_ACTIVE_TASKS: process.env.DEFAULT_ORG_MAX_ACTIVE_TASKS,
    SHARE_SNAPSHOT_FALLBACK_ALLOWED: process.env.SHARE_SNAPSHOT_FALLBACK_ALLOWED,
    UPLOAD_STORAGE_PROVIDER: process.env.UPLOAD_STORAGE_PROVIDER,

    // PromptLayer
    PROMPTLAYER_API_KEY: process.env.PROMPTLAYER_API_KEY,
    PROMPTLAYER_TEMPLATE_NAME: process.env.PROMPTLAYER_TEMPLATE_NAME,
    PROMPTLAYER_TEMPLATE_VERSION: process.env.PROMPTLAYER_TEMPLATE_VERSION,
    PROMPTLAYER_TEMPLATE_LABEL: process.env.PROMPTLAYER_TEMPLATE_LABEL,
    PROMPTLAYER_PROVIDER: process.env.PROMPTLAYER_PROVIDER,
    PROMPTLAYER_WORKSPACE_ID: process.env.PROMPTLAYER_WORKSPACE_ID,
    WELCOME_TEMPLATE_PROJECT_ID: process.env.WELCOME_TEMPLATE_PROJECT_ID,

    NEWAPI_BASE_URL: process.env.NEWAPI_BASE_URL,
    NEWAPI_ADMIN_TOKEN: process.env.NEWAPI_ADMIN_TOKEN,
    NEWAPI_ADMIN_USER_ID: process.env.NEWAPI_ADMIN_USER_ID,

    AZURE_VIDEO_INDEXER_ACCOUNT_ID: process.env.AZURE_VIDEO_INDEXER_ACCOUNT_ID,
    AZURE_VIDEO_INDEXER_SUBSCRIPTION_KEY: process.env.AZURE_VIDEO_INDEXER_SUBSCRIPTION_KEY,
    AZURE_VIDEO_INDEXER_LOCATION: process.env.AZURE_VIDEO_INDEXER_LOCATION,
    AZURE_VIDEO_INDEXER_ACCESS_TOKEN: process.env.AZURE_VIDEO_INDEXER_ACCESS_TOKEN,
  },
});
